import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { AlertCircle, CheckCircle, XCircle, Clock, Check } from 'lucide-react';
import api, { formatErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import BulkActionsToolbar from '@/components/BulkActionsToolbar';
import CustomCheckbox from '@/components/Checkbox';

const Alerts = () => {
  const [alerts, setAlerts] = useState([]);
  const [filteredAlerts, setFilteredAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterNiveau, setFilterNiveau] = useState('all');
  const [filterResolved, setFilterResolved] = useState('unresolved');
  
  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [markAsReadConfirmOpen, setMarkAsReadConfirmOpen] = useState(false);
  const [deleteReadConfirmOpen, setDeleteReadConfirmOpen] = useState(false);

  useEffect(() => {
    fetchAlerts();
  }, []);

  useEffect(() => {
    let filtered = alerts;

    if (filterNiveau !== 'all') {
      filtered = filtered.filter((a) => a.niveau === filterNiveau);
    }

    if (filterResolved === 'resolved') {
      filtered = filtered.filter((a) => a.resolved_at !== null);
    } else if (filterResolved === 'unresolved') {
      filtered = filtered.filter((a) => a.resolved_at === null);
    }

    setFilteredAlerts(filtered);
  }, [alerts, filterNiveau, filterResolved]);

  const fetchAlerts = async () => {
    try {
      const response = await api.get('/alerts');
      setAlerts(response.data);
      setFilteredAlerts(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des alertes');
    } finally {
      setLoading(false);
    }
  };


  // Bulk selection handlers
  const handleSelectAll = () => {
    setSelectedIds(filteredAlerts.map(a => a.id));
  };

  const handleDeselectAll = () => {
    setSelectedIds([]);
  };

  const handleToggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) 
        ? prev.filter(selectedId => selectedId !== id)
        : [...prev, id]
    );
  };

  const handleBulkDelete = () => {
    setDeleteConfirmOpen(true);
  };

  const handleBulkMarkAsRead = () => {
    setMarkAsReadConfirmOpen(true);
  };

  const handleBulkDeleteRead = () => {
    setDeleteReadConfirmOpen(true);
  };

  const confirmBulkDelete = async () => {
    try {
      await api.post('/alerts/bulk-delete', { ids: selectedIds });
      toast.success(`${selectedIds.length} alerte(s) supprimée(s)`);
      setSelectedIds([]);
      setDeleteConfirmOpen(false);
      fetchAlerts();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Erreur lors de la suppression'));
      console.error('Delete error:', error);
    }
  };

  const confirmBulkMarkAsRead = async () => {
    try {
      await api.post('/alerts/bulk-mark-as-read', { ids: selectedIds });
      toast.success(`${selectedIds.length} alerte(s) marquée(s) comme lue(s)`);
      setSelectedIds([]);
      setMarkAsReadConfirmOpen(false);
      fetchAlerts();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Erreur'));
      console.error('Mark as read error:', error);
    }
  };

  const confirmBulkDeleteRead = async () => {
    try {
      const response = await api.post('/alerts/bulk-delete-read');
      toast.success(response.data.message || 'Alertes lues supprimées');
      setDeleteReadConfirmOpen(false);
      fetchAlerts();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Erreur'));
      console.error('Delete read error:', error);
    }
  };

  const handleResolve = async (alertId) => {
    try {
      await api.put(`/alerts/${alertId}/resolve`);
      toast.success('Alerte résolue');
      fetchAlerts();
    } catch (error) {
      toast.error('Erreur lors de la résolution');
    }
  };

  const getNiveauIcon = (niveau) => {
    switch (niveau) {
      case 'critical':
        return <XCircle className="h-5 w-5 text-red-600" />;
      case 'warning':
        return <AlertCircle className="h-5 w-5 text-amber-600" />;
      case 'info':
        return <Clock className="h-5 w-5 text-blue-600" />;
      default:
        return <AlertCircle className="h-5 w-5" />;
    }
  };

  const getNiveauBadge = (niveau) => {
    const badges = {
      critical: 'bg-red-100 text-red-700 border-red-200',
      warning: 'bg-amber-100 text-amber-700 border-amber-200',
      info: 'bg-blue-100 text-blue-700 border-blue-200',
    };
    return badges[niveau] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  const unresolvedCount = alerts.filter((a) => !a.resolved_at).length;
  const criticalCount = alerts.filter((a) => a.niveau === 'critical' && !a.resolved_at).length;

  return (
    <div className="p-8 space-y-6" data-testid="alerts-page">
      <div>
        <h1 className="text-4xl font-bold text-gray-900">Centre d'alertes</h1>
        <p className="text-gray-600 mt-2">
          {unresolvedCount} alerte{unresolvedCount > 1 ? 's' : ''} non résolue{unresolvedCount > 1 ? 's' : ''}
          {criticalCount > 0 && (
            <span className="ml-2 text-red-600 font-medium">
              ({criticalCount} critique{criticalCount > 1 ? 's' : ''})
            </span>
          )}
        </p>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-md">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="space-y-1 flex-1 min-w-[200px]">
              <label className="text-sm font-medium text-gray-700">Niveau</label>
              <Select value={filterNiveau} onValueChange={setFilterNiveau}>
                <SelectTrigger data-testid="filter-niveau-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les niveaux</SelectItem>
                  <SelectItem value="critical">Critique</SelectItem>
                  <SelectItem value="warning">Avertissement</SelectItem>
                  <SelectItem value="info">Info</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1 flex-1 min-w-[200px]">
              <label className="text-sm font-medium text-gray-700">Statut</label>
              <Select value={filterResolved} onValueChange={setFilterResolved}>
                <SelectTrigger data-testid="filter-resolved-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes</SelectItem>
                  <SelectItem value="unresolved">Non résolues</SelectItem>
                  <SelectItem value="resolved">Résolues</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Actions Toolbar */}
      <BulkActionsToolbar
        selectedCount={selectedIds.length}
        totalCount={filteredAlerts.length}
        onSelectAll={handleSelectAll}
        onDeselectAll={handleDeselectAll}
        onBulkDelete={handleBulkDelete}
        onBulkAction={handleBulkMarkAsRead}
        bulkActionLabel="Marquer comme lues"
        bulkActionIcon={<Check className="h-4 w-4" />}
        entityName="alerte"
      />

      {/* Button to delete all read alerts */}
      {alerts.some(a => a.resolved_at) && (
        <Card className="bg-yellow-50 border-yellow-200">
          <CardContent className="p-4 flex items-center justify-between">
            <span className="text-sm text-yellow-800">
              Des alertes résolues sont encore présentes dans la liste
            </span>
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleBulkDeleteRead}
              className="text-yellow-800 border-yellow-300 hover:bg-yellow-100"
            >
              Supprimer toutes les alertes lues
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Alerts List */}
      <div className="space-y-3">
        {filteredAlerts.map((alert) => (
          <Card
            key={alert.id}
            className={`border shadow-sm transition-all hover:shadow-md ${
              alert.resolved_at ? 'opacity-60' : ''
            }`}
          >
            <CardContent className="p-5">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 mt-1">
                  <CustomCheckbox
                    checked={selectedIds.includes(alert.id)}
                    onChange={() => handleToggleSelect(alert.id)}
                  />
                </div>
                <div className="flex-shrink-0 mt-1">{getNiveauIcon(alert.niveau)}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getNiveauBadge(alert.niveau)}`}>
                          {alert.niveau === 'critical' ? 'Critique' : alert.niveau === 'warning' ? 'Avertissement' : 'Info'}
                        </span>
                        {alert.resolved_at && (
                          <span className="flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 border border-green-200">
                            <CheckCircle className="h-3 w-3" />
                            Résolu
                          </span>
                        )}
                      </div>
                      <p className="text-gray-900 font-medium mb-1">{alert.message}</p>
                      <p className="text-sm text-gray-500">
                        Créée le {new Date(alert.created_at).toLocaleDateString('fr-FR')} à {new Date(alert.created_at).toLocaleTimeString('fr-FR')}
                      </p>
                      {alert.due_at && (
                        <p className="text-sm text-gray-500">
                          Échéance : {new Date(alert.due_at).toLocaleDateString('fr-FR')}
                        </p>
                      )}
                    </div>
                    {!alert.resolved_at && (
                      <Button
                        onClick={() => handleResolve(alert.id)}
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-700 flex-shrink-0"
                        data-testid={`resolve-alert-${alert.id}`}
                      >
                        Marquer comme résolu
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredAlerts.length === 0 && (
        <Card className="border-0 shadow-md">
          <CardContent className="p-12">
            <div className="text-center text-gray-500">
              <CheckCircle className="h-12 w-12 mx-auto mb-3 text-emerald-600" />
              <p className="text-lg font-medium">Aucune alerte à afficher</p>
              <p className="text-sm mt-1">Toutes les alertes sont résolues ou aucune alerte ne correspond aux filtres</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmer la suppression</DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer {selectedIds.length} alerte(s) ?
              Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>
              Annuler
            </Button>
            <Button variant="destructive" onClick={confirmBulkDelete}>
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Mark As Read Confirmation Dialog */}
      <Dialog open={markAsReadConfirmOpen} onOpenChange={setMarkAsReadConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Marquer comme lues</DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir marquer {selectedIds.length} alerte(s) comme lues ?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMarkAsReadConfirmOpen(false)}>
              Annuler
            </Button>
            <Button onClick={confirmBulkMarkAsRead}>
              Marquer comme lues
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Read Confirmation Dialog */}
      <Dialog open={deleteReadConfirmOpen} onOpenChange={setDeleteReadConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Supprimer les alertes lues</DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer toutes les alertes déjà lues ?
              Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteReadConfirmOpen(false)}>
              Annuler
            </Button>
            <Button variant="destructive" onClick={confirmBulkDeleteRead}>
              Supprimer les alertes lues
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Alerts;
